import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail } from 'lucide-react';
import { Switch } from '../ui/switch';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const GROUPS = [
  { title: 'Instant emails (one per punch)', items: [
    { key: 'realtime_alert_emails', label: 'All instant "[Clock alert]" emails', hint: 'Master switch — off stops every instant clock email' },
    { key: 'email_off_network', label: 'Off-network punches', hint: 'Clock-in from a network that is not the office Wi-Fi', sub: true },
    { key: 'email_face_mismatch', label: 'Face mismatch punches', hint: "Selfie doesn't match the profile photo", sub: true },
  ] },
  { title: 'Daily summary emails', items: [
    { key: 'email_digest_spoofing', label: '"[Spoofing alert]" summary', hint: 'Daily count of off-network clock-ins' },
    { key: 'email_digest_review', label: '"[Clock review]" summary', hint: 'Daily list of other flagged punches' },
    { key: 'email_digest_hr_action', label: '"[HR action needed]" summary', hint: 'Stale leave approvals, visa cancellations, ILOE expiry' },
  ] },
];

export const ClockAlertEmailSwitches = ({ isViewer = false, compact = false }) => {
  const [vals, setVals] = useState(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    axios.get(`${API}/api/settings/clock-antispoof`, { headers })
      .then(r => setVals(r.data?.current || {}))
      .catch(() => setVals({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (key, v) => {
    setVals(p => ({ ...p, [key]: v }));
    try {
      await axios.put(`${API}/api/settings/clock-antispoof`, { [key]: v }, { headers });
      toast({ title: v ? 'Email turned on' : 'Email turned off' });
    } catch (e) {
      setVals(p => ({ ...p, [key]: !v }));
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  if (!vals) return null;
  const master = vals.realtime_alert_emails ?? true;

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3" data-testid="clock-alert-emails-panel">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
        <Mail className="h-4 w-4 text-violet-600" /> Clock alert emails
        {!compact && <span className="text-[11px] font-normal text-gray-500">— only emails stop; in-app alerts and the Flagged list keep working</span>}
      </div>
      <div className={compact ? 'grid gap-3 md:grid-cols-2' : 'space-y-3'}>
        {GROUPS.map(g => (
          <div key={g.title} className="space-y-1.5">
            <p className="text-[11px] uppercase tracking-wide text-gray-500">{g.title}</p>
            {g.items.map(it => (
              <div key={it.key} className={`flex items-center justify-between gap-3 ${it.sub ? 'pl-4' : ''}`}>
                <div>
                  <p className="text-sm text-gray-800">{it.label}</p>
                  <p className="text-[11px] text-gray-500">{it.hint}</p>
                </div>
                <Switch checked={vals[it.key] ?? true} disabled={isViewer || (it.sub && !master)}
                  onCheckedChange={v => save(it.key, v)} data-testid={`clock-email-toggle-${it.key}`} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
